// src/components/noticeboard/noticeboardApi.js
//
// The read side of the noticeboard (Noticeboard contract v1.1, 3 Oct 2026):
//   GET /api/noticeboard?surface=suite|kuestion   — public, no token, flat
//   envelope {status, notices[], generated_at}, Cache-Control max-age=60 + ETag.
//
// Fail soft, always: a network failure, a 404 or a 5xx is `notices: []`. A
// broken noticeboard must never look like an outage.
//
// Acknowledgement is per browser and per key, never on the account: the board
// is shown before anyone signs in, and nothing about a notice is personal.
import { API_ENDPOINTS } from '../../config/apiConfig';

export const FRESH_MS = 10 * 60 * 1000; // refetch on focus only when older than this

const cache = {};     // surface -> { notices, at }
const inflight = {};  // surface -> Promise
let autoOpened = false; // the board opens by itself once per page load at most

const KINDS = ['maintenance', 'incident', 'info'];
const valid = (n) => n && typeof n.id === 'string' && typeof n.heading === 'string';

// date desc, then updated_at desc — the server's order, kept stable here.
const sorted = (list) => list.slice().sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))
  || String(b.updated_at || '').localeCompare(String(a.updated_at || '')));

export const cachedNotices = (surface) => (cache[surface] ? cache[surface].notices : null);
export const isStale = (surface) => !cache[surface] || Date.now() - cache[surface].at > FRESH_MS;

export function loadNotices(surface, { force = false } = {}) {
  const c = cache[surface];
  if (c && !force && Date.now() - c.at < FRESH_MS) return Promise.resolve(c.notices);
  if (inflight[surface]) return inflight[surface];
  inflight[surface] = (async () => {
    try {
      if (typeof fetch !== 'function') return [];
      // No custom headers: the request stays "simple", so no CORS preflight,
      // and the browser's own cache does the ETag / 304 revalidation.
      const res = await fetch(API_ENDPOINTS.NOTICEBOARD(surface), { method: 'GET' });
      if (!res || !res.ok) return [];
      const json = await res.json();
      const list = sorted((Array.isArray(json?.notices) ? json.notices : []).filter(valid)
        .map((n) => ({ ...n, kind: KINDS.includes(n.kind) ? n.kind : 'info', revision: Number(n.revision) || 1 })));
      cache[surface] = { notices: list, at: Date.now() };
      return list;
    } catch {
      return [];
    } finally {
      delete inflight[surface];
    }
  })();
  return inflight[surface];
}

/** `id@revision`: a material edit bumps the revision, so the notice reads as new again. */
export const tokenOf = (n) => `${n.id}@${n.revision || 1}`;

export const seenKey = (store) => `${store === 'kq' ? 'kq' : 'rw'}-notice-seen`;

export function readSeen(store) {
  try {
    const v = JSON.parse(localStorage.getItem(seenKey(store)) || '[]');
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}

/** Writes exactly the tokens currently listed, which prunes notices no longer live. */
export function writeSeen(store, notices) {
  const tokens = notices.map(tokenOf);
  try { localStorage.setItem(seenKey(store), JSON.stringify(tokens)); } catch { /* private mode */ }
  return tokens;
}

/** True the first time it is asked in a page load, false after. */
export function claimAutoOpen() {
  if (autoOpened) return false;
  autoOpened = true;
  return true;
}

/** Tests only: forget the cache and the once-per-load flag. */
export function __resetNoticeboard() {
  Object.keys(cache).forEach((k) => delete cache[k]);
  Object.keys(inflight).forEach((k) => delete inflight[k]);
  autoOpened = false;
}
